import React from 'react';
import { Droppable } from '@hello-pangea/dnd';

// Design System Components
import Typography from 'Components/DesignSystem/Typography/Typography';

// Module Components
import PreviewBlock from './PreviewBlock';
// Region caption hidden (2026-09-28) — see the commented-out <RegionHeader> below.
// import RegionHeader from './RegionHeader';

// Constants
import { CARD_BLOCK_DND_TYPE, FLOATING_DROPPABLE_ID, STRINGS } from '../../../constants';

// Types
import { BlockPlacement, FieldId, FieldConfiguration, RegionId } from '../../../types';

interface FloatingRegionProps {
  /** Read-only — shared with the document index (state/cardIndex). */
  blocks: readonly BlockPlacement[];
  /** The footer strip only exists while "Add a footer" is on (spec R53). */
  footerEnabled: boolean;
  isDragging: boolean;
  activeRegion: RegionId | null;
  selectedFieldId: FieldId | null;
  configurationFor: (fieldId: FieldId) => FieldConfiguration;
  /** Card-wide: price rows carry the unit of measure (spec R69, R72). */
  onSelectBlock: (fieldId: FieldId) => void;
  onDeleteBlock: (fieldId: FieldId) => void;
}

/**
 * The Floating region: a single sticky strip pinned to the bottom of the card.
 *
 * It stays exposed and labelled even with the footer switched off, so all three
 * regions are always named (spec R48) — but with no footer there is nowhere to
 * pin a block, so the target is disabled and says so rather than silently
 * swallowing a drop. Switching the footer back off returns any block here to the
 * Info stack (spec R53), which the reducer's invariants handle.
 *
 * An empty footer reads as its placeholder at rest and as its drop target during a
 * drag (spec R48) — copy only: the strip keeps its idle size either way, because a
 * target that grows once a drag starts moves everything measured after it.
 */
const FloatingRegion = ({
  blocks,
  footerEnabled,
  isDragging,
  // `activeRegion` only fed the region caption, hidden 2026-09-28; the prop stays on the
  // interface so CardPreview is unchanged. Restore with the caption.
  // activeRegion,
  selectedFieldId,
  configurationFor,
  onSelectBlock,
  onDeleteBlock,
}: FloatingRegionProps) => {
  const emptyFooterCopy = isDragging
    ? STRINGS.preview.footerDropTarget
    : STRINGS.preview.footerEmpty;

  return (
  <div className="tw-mt-1">
    {/* Region caption hidden on request (2026-09-28): the preview no longer labels this region.
        Restore by uncommenting this and the RegionHeader import above.
    <RegionHeader
      region="floating"
      isDragging={isDragging}
      isActive={activeRegion === 'floating'}
    /> */}

    <Droppable
      droppableId={FLOATING_DROPPABLE_ID}
      direction="horizontal"
      type={CARD_BLOCK_DND_TYPE}
      isDropDisabled={!footerEnabled}
    >
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.droppableProps}
          className={`tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-min-h-[40px] tw-border-t tw-border-edge tw-rounded-sm ${
            footerEnabled ? 'tw-bg-surface-sunken' : 'tw-bg-surface'
          } ${
            snapshot.isDraggingOver
              ? 'tw-outline tw-outline-2 tw-outline-accent'
              : isDragging && footerEnabled
                ? 'tw-outline-dashed tw-outline-1 tw-outline-accent'
                : ''
          }`}
        >
          {blocks.map((placement, index) => (
            <PreviewBlock
              key={placement.fieldId}
              placement={placement}
              configuration={configurationFor(placement.fieldId)}
              index={index}
              isSelected={selectedFieldId === placement.fieldId}
              onSelect={() => onSelectBlock(placement.fieldId)}
              onDelete={() => onDeleteBlock(placement.fieldId)}
            />
          ))}
          {provided.placeholder}

          {blocks.length === 0 && (
            <Typography
              variant="caption-metadata"
              as="span"
              className={
                isDragging && footerEnabled ? 'tw-text-accent' : 'tw-text-ink-tertiary'
              }
            >
              {footerEnabled ? emptyFooterCopy : STRINGS.preview.footerDisabled}
            </Typography>
          )}
        </div>
      )}
    </Droppable>
  </div>
  );
};

export default FloatingRegion;
